<template>
  <layout-default>
    <div class="courses" v-if="!isCurrentPageRootLevel">
      <v-overlay :value="loadingCoursesList" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-tabs
        class="courses__tabs"
        v-model="listActiveTab"
        background-color="transparent"
        color="accent"
        @change="changeActiveListTab"
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab
          v-for="(tab, index) in listTabs"
          :key="`courses-list-tab-${index}`"
          :href="`#${tab}`"
          :tab-value="tab"
          >{{ tab }}</v-tab
        >
      </v-tabs>
      <v-tabs-items v-model="listActiveTab" class="courses__list-tabs-items">
        <v-tab-item
          v-for="(tab, index) in listTabs"
          :value="tab"
          :key="`courses-list-tab-item-${index}`"
        >
          <v-expansion-panels
            v-if="infoBars[tab]"
            v-model="activeInfoBar"
            class="my-4"
            background-color="transparent"
          >
            <v-expansion-panel>
              <v-expansion-panel-header
                v-if="infoBars[tab].title"
                :class="activeInfoBar ? 'pb-0' : ''"
              >
                <h5 class="bhBlack--text mb-0">{{ infoBars[tab].title }}</h5>
              </v-expansion-panel-header>
              <v-expansion-panel-content v-if="infoBars[tab].description">
                <p
                  class="courses__info-description mb-0"
                  v-html="infoBars[tab].description"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <template v-if="tab === listTabsAvailable.MY_COURSES">
            <v-list
              class="courses-list"
              v-if="
                (loadingMyCourses && (!myCourses || !myCourses.length)) ||
                  (!loadingMyCourses && myCourses && myCourses.length)
              "
            >
              <courses-list-header :is-advisor="isAdvisor" />
              <courses-list-item
                v-for="(course, courseIndex) in myCourses"
                :key="`course-list-item-${course.id}-${index}-${courseIndex}`"
                :id="course.id"
                :title="course.title"
                :description="course.description"
                :rating="course.rating"
                :created-by="course.createdBy"
                :created-date="course.createdDate"
                :visibility-status="course.visibilityStatus"
                :assignment-status="course.assignmentStatus"
                :enrolled-count="course.enrolledCount"
                :participants-count="course.participantsCount"
                :participants="course.participants"
                :static-media-thumbnail-url="course.staticMediaThumbnailUrl"
                :thumbnail-url="course.thumbnailUrl"
                :media-url="course.mediaUrl"
                @navigate-to-course="navigateToCourse"
                @open-change-visibility="openChangeVisibilityDialog($event)"
                @open-assign-participants="
                  openParticipantSelectorDialog($event)
                "
              />
            </v-list>
            <div
              class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
              v-else
            >
              <h4 class="mb-4">There are no courses.</h4>
              <v-btn
                v-if="isAdvisor"
                class="rounded-pill"
                color="accent"
                @click="navigateToCreateCourse"
                ><v-icon class="mr-2">bhi-plus</v-icon>Create course</v-btn
              >
            </div>
          </template>
          <template v-if="tab === listTabsAvailable.LIBRARY">
            <v-list
              class="courses-list"
              v-if="
                (loadingLibraryCourses &&
                  (!libraryCourses || !libraryCourses.length)) ||
                  (!loadingLibraryCourses &&
                    libraryCourses &&
                    libraryCourses.length)
              "
            >
              <courses-list-header
                :is-library-item="true"
                :is-advisor="isAdvisor"
              />
              <courses-list-item
                v-for="(course, courseIndex) in libraryCourses"
                :key="`course-list-item-${course.id}-${index}-${courseIndex}`"
                :id="course.id"
                :title="course.title"
                :description="course.description"
                :rating="course.rating"
                :created-by="course.createdBy"
                :created-date="course.createdDate"
                :visibility-status="course.visibilityStatus"
                :assignment-status="course.assignmentStatus"
                :enrolled-count="course.enrolledCount"
                :participants-count="course.participantsCount"
                :participants="course.participants"
                :static-media-thumbnail-url="course.staticMediaThumbnailUrl"
                :thumbnail-url="course.thumbnailUrl"
                :media-url="course.mediaUrl"
                :is-library-item="true"
                @navigate-to-course="navigateToCourse"
                @import-course="importCourse"
                @open-change-visibility="openChangeVisibilityDialog($event)"
                @open-assign-participants="
                  openParticipantSelectorDialog($event)
                "
              />
            </v-list>
            <div
              class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
              v-else
            >
              <h4 class="mb-4">There are no courses.</h4>
              <v-btn
                class="rounded-pill"
                color="accent"
                @click="navigateToCreateCourse"
                v-if="isAdvisor"
                ><v-icon class="mr-2">bhi-plus</v-icon>Create course</v-btn
              >
            </div>
          </template>
        </v-tab-item>
      </v-tabs-items>
      <template v-if="isCompanyUser">
        <v-btn
          elevation="2"
          fab
          bottom
          right
          @click="navigateToCreateCourse"
          color="accent"
          class="courses__fab"
        >
          <v-icon>bhi-plus</v-icon>
        </v-btn>
      </template>
      <normal-dialog
        title="Change status"
        content-class="courses__visibility-dialog"
        color="accent"
        :showDialog="changeVisibilityDialogActive"
        @cancel="onCloseChangeVisibilityDialog"
        ref="visibilityStatusForm"
      >
        <v-list>
          <v-list-item-group v-model="visibilityStatusValue" color="accent">
            <v-list-item
              v-for="(item, i) in COURSES_VISIBILITY_STATUS_OPTIONS"
              :value="item.value"
              :key="i"
              class="px-6"
            >
              <v-list-item-content>
                <v-list-item-title>{{
                  item.selectText || item.text
                }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon v-if="item.value === visibilityStatusValue">
                <v-icon>bhi-check</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list-item-group>
        </v-list>
        <template v-slot:actions>
          <div class="courses__visibility-dialog-actions px-2">
            <v-btn
              color="accent"
              class="rounded-pill"
              @click="submitChangeVisibilityDialog"
              >Update</v-btn
            >
            <v-btn
              color="accent"
              class="rounded-pill"
              text
              @click="closeChangeVisibilityDialog"
              >Cancel</v-btn
            >
          </div>
        </template>
      </normal-dialog>
      <normal-dialog
        :max-width="448"
        :showDialog.sync="participantSelectorDialogActive"
        content-class="participant-selector-dialog ma-4 ml-auto"
        title="Select participants"
        :full-height="true"
        ref="participantSelectorDialog"
        color="accent"
        @cancel="afterCloseParticipantSelectorDialog"
      >
        <v-overlay :value="loadingCurrentCourseParticipants" z-index="4">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <participant-selector
          :key="
            `participants-${participantSelectorDialogActive ? 'on' : 'off'}`
          "
          ref="participantSelector"
          :initial-select-all="initialSelectAll"
          :initial-select-all-and-future="initialSelectAllAndFuture"
          :initial-selection="initialSelection"
          initial-selection-prop="participantId"
          :all-enabled="true"
          :all-and-future-enabled="true"
        />
        <template v-slot:actions>
          <v-btn
            elevation="2"
            bottom
            right
            @click="applyAssignParticipants"
            color="accent"
            class="participant-selector-dialog__apply rounded-pill"
            :loading="loadingCurrentCourse"
          >
            Apply
          </v-btn>
        </template>
      </normal-dialog>
    </div>
    <router-view else />
  </layout-default>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import CoursesListItem from '@/components/courses/CoursesListItem.vue'
import CoursesListHeader from '@/components/courses/CoursesListHeader.vue'
import NormalDialog from '@/components/dialogs/NormalDialog.vue'
import ParticipantSelector from '@/components/inputs/ParticipantSelector.vue'
import { COURSES_VISIBILITY_STATUS_OPTIONS } from '@/helpers/constants.js'

const LIST_TABS_ONLY_LIBRARY = Object.freeze({
  LIBRARY: 'Library',
})
const LIST_TABS_ALL = Object.freeze({
  MY_COURSES: 'My Courses',
  LIBRARY: 'Library',
})
const INFO_BARS = Object.freeze({
  [LIST_TABS_ALL.MY_COURSES]: {
    title: 'Info',
    description:
      'Below you see all Courses you created or got access to via newly assigned Participants to you. Courses you create are private to you, unless you share it to your Company Library.\nAll changes you make to Courses are immediately visible to Participants active in the Course.',
  },
  [LIST_TABS_ALL.LIBRARY]: {
    title: 'Info',
    description:
      'Library is the Company space of all shared Courses. You can contribute to the Library by changing status of your Course to "Available in Library for all". Other Advisors will see your shared Courses and can use them directly or import a copy of your Course to their private Library. You can do the same, assign Courses from Library to your Participants directly or import a copy to make changes.\n\nPlease note, all changes you make to shared Courses, it affects other users of those Courses immediately. The same happens to Participants you assign directly from Library when Course author makes changes.',
  },
})

export default {
  name: 'Courses',
  components: {
    LayoutDefault,
    CoursesListItem,
    CoursesListHeader,
    NormalDialog,
    ParticipantSelector,
  },
  mixins: [],
  props: {},
  data() {
    const defaultTabs = LIST_TABS_ONLY_LIBRARY
    return {
      COURSES_VISIBILITY_STATUS_OPTIONS,
      infoBars: INFO_BARS,
      activeInfoBar: false,
      listTabsAvailable: defaultTabs,
      listTabs: Object.values(defaultTabs),
      listActiveTab: Object.values(defaultTabs)?.[0],
      changeVisibilityDialogActive: false,
      visibilityStatusValue: 'DRAFT',
      visibilityStatusCourse: null,
      participantSelectorDialogActive: false,
      courseIdForParticipantSelector: null,
      initialSelectAll: false,
      initialSelectAllAndFuture: false,
      initialSelection: [],
    }
  },
  computed: {
    isCurrentPageRootLevel() {
      return this.$route.meta.root
    },
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('coursesStates', [
      'libraryCourses',
      'myCourses',
      'loadingLibraryCourses',
      'loadingMyCourses',
      'loadingCurrentCourse',
      'currentCourseAssignmentStatus',
      'currentCourseParticipants',
      'loadingCurrentCourseParticipants',
    ]),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    isCompanyUser() {
      // is Company Admin, Company Advisor or Company Team
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Team
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    loadingCoursesList() {
      return this.loadingMyCourses || this.loadingLibraryCourses
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    ...mapActions('coursesStates', [
      'getCourseDefinitions',
      'updateCourseDefinition',
      'importCourseDefinition',
      'assignCourseDefinition',
      'getCourseDefinitionParticipants',
    ]),
    ...mapMutations('coursesStates', [
      'setLoadingCurrentCourse',
      'setVisibilityStatusOfCourse',
    ]),
    getCourses(tab) {
      if (tab === this.listTabsAvailable.MY_COURSES) {
        this.getCourseDefinitions({ library: false })
      } else if (tab === this.listTabsAvailable.LIBRARY) {
        this.getCourseDefinitions({ library: true })
      }
    },
    changeActiveListTab(tab) {
      this.getCourses(tab)
    },
    navigateToCourse(id) {
      this.$router.push({
        name: 'Course Details',
        path: '/courses/:id',
        params: { id },
      })
    },
    navigateToCreateCourse() {
      this.$router.push({
        name: 'Create Course',
        path: '/courses/add',
      })
    },
    importCourse(id) {
      this.importCourseDefinition({ id })
    },
    init() {
      if (!this.isCurrentPageRootLevel) {
        if (this.isCompanyUser && this.isAdvisor) {
          // Check if listTabs is different
          if (
            JSON.stringify(this.listTabsAvailable) !==
            JSON.stringify(LIST_TABS_ALL)
          ) {
            this.listTabsAvailable = LIST_TABS_ALL
            return
          }
        }
        this.getCourses(this.listActiveTab)
      }
    },
    openChangeVisibilityDialog(course = null) {
      if (course === null || course?.id === null) {
        return
      }

      this.visibilityStatusCourse = course || null
      this.visibilityStatusValue = course?.visibilityStatus || null
      this.changeVisibilityDialogActive = true
    },
    onCloseChangeVisibilityDialog() {
      this.changeVisibilityDialogActive = false
      this.visibilityStatusCourse = null
    },
    closeChangeVisibilityDialog() {
      this.$refs?.['visibilityStatusForm']?.cancel?.()
    },
    async submitChangeVisibilityDialog() {
      if (this.visibilityStatusValue === null) {
        return
      }
      const isNewStatusValid = Object.values(COURSES_VISIBILITY_STATUS_OPTIONS)
        .map(el => el.value)
        .includes(this.visibilityStatusValue)

      if (!isNewStatusValid) {
        return
      }

      const activeTab = this.listActiveTab
      const courseType =
        activeTab === this.listTabsAvailable.MY_COURSES
          ? 'MY_COURSES'
          : activeTab === this.listTabsAvailable.LIBRARY
          ? 'LIBRARY'
          : null

      const {
        id,
        title,
        description,
        visibilityStatus: oldVisibilityStatus,
      } = this.visibilityStatusCourse
      this.closeChangeVisibilityDialog()
      try {
        this.setVisibilityStatusOfCourse({
          id,
          visibilityStatus: this.visibilityStatusValue,
          courseType,
        })
        await this.updateCourseDefinition({
          data: {
            id,
            title,
            description,
            visibilityStatus: this.visibilityStatusValue,
          },
        })
      } catch (error) {
        this.setVisibilityStatusOfCourse({
          id,
          visibilityStatus: oldVisibilityStatus,
          courseType,
        })
      }
    },
    async applyAssignParticipants() {
      try {
        const {
          allSelected = false,
          allAndFutureSelected = false,
          selectedParticipants = [],
        } = this.$refs['participantSelector']

        const selectedParticipantsIds = selectedParticipants
          .map(el => el?.id || el?.participantId || null)
          .filter(el => el !== null)

        const response = await this.assignCourseDefinition({
          id: this.courseIdForParticipantSelector,
          all: allSelected,
          autoAssignAll: allAndFutureSelected,
          participants:
            allSelected || allAndFutureSelected
              ? []
              : [...selectedParticipantsIds],
        })

        if (response === true) {
          // success response
          this.closeParticipantSelectorDialog()

          // refresh list
          this.getCourses(this.listActiveTab)
        }
      } catch (error) {
        // do nothing
      }
    },
    async openParticipantSelectorDialog(course = null) {
      if (course === null || course?.id === null) {
        return
      }

      this.courseIdForParticipantSelector = course.id
      this.getCourseDefinitionParticipants({ id: course.id })
      this.participantSelectorDialogActive = true
      this.setLoadingCurrentCourse(false)
    },
    closeParticipantSelectorDialog() {
      this.$refs?.['participantSelectorDialog']?.cancel?.()
    },
    afterCloseParticipantSelectorDialog() {
      this.initialSelectAll = false
      this.initialSelectAllAndFuture = false
      this.initialSelection = []
      this.participantSelectorDialogActive = false
      this.courseIdForParticipantSelector = null
      this.$store.commit('coursesStates/setCurrentCourseParticipants', [])
    },
  },
  watch: {
    loadingCurrentCourseParticipants(val) {
      if (!val) {
        this.initialSelection = [...(this.currentCourseParticipants || [])]
        this.initialSelectAll = this.currentCourseAssignmentStatus === 'ALL'
        this.initialSelectAllAndFuture =
          this.currentCourseAssignmentStatus === 'ALL'
        this.$refs?.['participantSelector']?.init?.()
      }
    },
    listTabsAvailable(val) {
      this.listTabs = Object.values(val)
      this.listActiveTab = Object.values(val)?.[0]
      this.getCourses(this.listActiveTab)
    },
    $route(to, from) {
      if (
        from?.name &&
        from?.name !== 'Courses' &&
        to?.name &&
        to?.name === 'Courses'
      ) {
        this.init()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.courses {
  padding: 2 * $gutter;
  height: 100%;
  display: flex;
  flex-direction: column;

  &__tabs {
    flex: initial;
  }

  &__list-tabs-items {
    height: 100%;
    overflow: auto;
    background-color: transparent !important;
  }

  &__fab {
    bottom: 0;
    right: 0;
    position: fixed;
    margin: 0 16px 16px 0;
  }

  &-list {
    background: transparent !important;
  }

  &__visibility-dialog {
    &-actions {
      width: 100%;
    }
    .normal-dialog__content {
      padding: 0 !important;
    }
  }

  &__info-description {
    white-space: break-spaces;
  }
}
</style>
